import {
    Link,
    X,
    Expand,
    MessageSquare,
    File,
    Download,
    CheckCircle
} from "lucide-react";
import React, { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";

const OriginalQuoteDetails = ({ quoteId, refId, currentQuote }) => {
    const [details, setDetails] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [activeOriginalTab, setActiveOriginalTab] = useState(0);
    const [mainTab, setMainTab] = useState("details");

    const [chatsData, setChatsData] = useState(null);
    const [loadingChats, setLoadingChats] = useState(false);

    const [feasibilityData, setFeasibilityData] = useState([]);
    const [loadingFeasibility, setLoadingFeasibility] = useState(false);

    console.log("quoteId is ", quoteId)

    const fetchChats = async (rId) => {
        setLoadingChats(true);
        try {
            const loopUserData = localStorage.getItem('loopuser');
            const loopUserObject = loopUserData ? JSON.parse(loopUserData) : null;

            const chatResponse = await fetch(
                "https://loopback-skci.onrender.com/api/scope/getQuoteChatByRefidApi",
                {
                    method: "POST",
                    headers: {
                        "Content-type": "application/json",
                    },
                    body: JSON.stringify({
                        ref_id: rId,
                        quote_id: quoteId,
                        user_id: loopUserObject?.id
                    }),
                }
            );
            const chatData = await chatResponse.json();
            if (chatData.status) {
                setChatsData(chatData.data);
            }
        } catch (error) {
            console.log("Error fetching chats:", error);
        } finally {
            setLoadingChats(false);
        }
    };

    const fetchFeasibilityComments = async (rId) => {
        setLoadingFeasibility(true);
        try {
            const response = await fetch(
                "https://loopback-skci.onrender.com/api/scope/getFeasibilityComments",
                {
                    method: "POST",
                    headers: {
                        "Content-type": "application/json",
                    },
                    body: JSON.stringify({
                        ref_id: rId,
                    }),
                }
            );
            const data = await response.json();
            if (data.status && data.data) {
                setFeasibilityData(data.data.feasability_comments || "");
            }
        } catch (error) {
            console.log("Error fetching feasibility comments:", error);
        } finally {
            setLoadingFeasibility(false);
        }
    };

    const parsePlanJson = (jsonString) => {
        if (!jsonString || typeof jsonString !== "string") return null;
        try {
            return JSON.parse(jsonString);
        } catch (err) {
            return null;
        }
    };

    const fetchDetails = async (quoteId) => {
        setLoading(true);
        try {
            const detailsResponse = await fetch(
                "https://loopback-skci.onrender.com/api/scope/adminScopeDetails",
                {
                    method: "POST",
                    headers: {
                        "Content-type": "application/json",
                    },
                    body: JSON.stringify({
                        quote_id: quoteId,
                        ref_id: refId
                    }),
                },
            );
            const detailsData = await detailsResponse.json();
            if (
                detailsData.status &&
                detailsData.quoteInfo
            ) {
                setDetails(detailsData.quoteInfo);
                if (detailsData.quoteInfo.length > 0) {
                    setActiveOriginalTab(0);
                }
            }

        } catch (error) {
            console.log("Error fetching original quote details:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (quoteId) {
            const cleanedId = quoteId.split(',').map(id => id.trim()).filter(id => id && id !== "null").join(',');
            if (cleanedId) {
                fetchDetails(cleanedId);
            }
        }
        if (refId) {
            fetchChats(refId);
            fetchFeasibilityComments(refId);
        }
    }, [quoteId, refId]);

    const formatMessage = (message) => {
        if (!message) return "";
        return message.replace(/\{\{\{(.*?),(.*?)\}\}\}/g, (match, name, id) => {
            return `<b style="color:#126dff;cursor:pointer;">${name}</b>`;
        });
    };

    const renderChatList = (chatArray) => {
        const loopUserData = localStorage.getItem('loopuser');
        const currentUser = loopUserData ? JSON.parse(loopUserData) : null;

        return chatArray.map((chatVal, idx) => {
            const isUser = chatVal.sender_id == currentUser?.id;
            return (
                <div key={idx} className={`flex flex-col mb-3 ${isUser ? 'items-end' : 'items-start'}`}>
                    <div className="flex items-center gap-2 mb-1">
                        <span className="text-[10px] font-bold text-gray-700">
                            {chatVal.fld_first_name} {chatVal.fld_last_name}
                        </span>
                        <span className="text-[9px] text-gray-500">
                            {chatVal.date ? new Date(chatVal.date * 1000).toLocaleString('en-GB', {
                                day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true
                            }).replace(',', '') : ""}
                        </span>
                    </div>
                    <div
                        className={`p-2 rounded-xl text-[11px] shadow-sm max-w-[85%] ${isUser ? 'bg-blue-100 text-blue-900 rounded-tr-none' : 'bg-gray-100 text-gray-800 rounded-tl-none'}`}
                        dangerouslySetInnerHTML={{ __html: formatMessage(chatVal.message) }}
                    />
                    {chatVal.isfile == "1" && chatVal.file_path && (
                        <div className="mt-1 flex items-center justify-between gap-3 text-[10px] bg-gray-50 p-1.5 rounded border border-gray-200">
                            <span className="truncate max-w-[150px]">{chatVal.file_path.split('/').pop()}</span>
                            <a href={chatVal.file_path} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">
                                <Download size={12} />
                            </a>
                        </div>
                    )}

                    {/* Replies */}
                    {chatVal.replies && chatVal.replies.length > 0 && (
                        <div className={`mt-2 w-[85%] space-y-2 flex flex-col ${isUser ? 'items-end' : 'items-start'}`}>
                            {chatVal.replies.map((reply, rIndex) => {
                                const isReplyUser = reply.sender_id == currentUser?.id || reply.fld_first_name == currentUser?.fld_first_name;
                                return (
                                    <div key={rIndex} className={`flex flex-col w-[90%] ${isReplyUser ? 'items-end' : 'items-start'}`}>
                                        <div className="flex items-center gap-2 mb-0.5">
                                            <span className="text-[9px] font-bold text-gray-600">{reply.fld_first_name} {reply.fld_last_name}</span>
                                        </div>
                                        <div className={`p-1.5 rounded-lg text-[10px] border ${isReplyUser ? 'bg-blue-50 text-blue-800 border-blue-100 rounded-tr-none' : 'bg-white text-gray-700 border-gray-200 rounded-tl-none'}`}>
                                            {reply.message}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            );
        });
    };

    // Helpers for rendering quote details inside the comparison cards
    const renderPriceAndMilestones = (info) => (
        <div className="grid grid-cols-2 gap-x-2 gap-y-1 mb-2">
            {info.price && (
                <div>
                    <span className="text-gray-500">Price:</span> <strong>{info.currency} {info.price}</strong>
                </div>
            )}
            {info.milestones && (
                <div>
                    <span className="text-gray-500">Milestones:</span> <strong>{info.milestones}</strong>
                </div>
            )}
            {(info.noOfWords || info.no_of_words) && (
                <div className="col-span-2">
                    <span className="text-gray-500">Words:</span> <strong>{info.noOfWords || info.no_of_words}</strong>
                </div>
            )}
        </div>
    );

    const renderMilestoneDetails = (info) => (
        info.milestoneData && info.milestoneData.length > 0 && (
            <div className="mt-2 pt-2 border-t border-gray-100">
                <div className="font-bold text-[10px] uppercase text-gray-400 mb-1">Milestones</div>
                <div className="space-y-2">
                    {info.milestoneData.map((ms, idx) => (
                        <div key={idx} className="bg-gray-50/50 p-1.5 rounded border border-gray-200">
                            <div className="flex justify-between items-center text-[10px]">
                                <div className="font-bold text-gray-700  mr-2" title={ms.name}>{ms.name}</div>
                                <div className="font-bold text-blue-600 whitespace-nowrap">{info.currency} {ms.price}</div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        )
    );

    const renderComparePlan = (label, info, isOriginal = false) => (
        <div
            className={`p-3 rounded-lg border shadow-sm h-full ${label === "Basic" ? "bg-blue-50 border-blue-200" : label === "Standard" ? "bg-gray-50 border-gray-200" : "bg-yellow-50 border-yellow-200"}`}
        >
            <div className={`font-bold text-[11px] mb-2 pb-1 border-b flex justify-between items-center ${label === "Basic" ? "border-blue-200 text-blue-800" : label === "Standard" ? "border-gray-300 text-gray-800" : "border-yellow-300 text-yellow-800"}`}>
                <span>{label} Plan</span>
                {isOriginal && <span className="text-[9px] bg-white/50 px-1 rounded border border-black/5 italic">Original</span>}
            </div>

            <div className="text-[11px] space-y-2">
                {renderPriceAndMilestones(info)}
                {renderMilestoneDetails(info)}

                {(info.remarks || info.tandc) && (
                    <div className="mt-2 pt-1 border-t border-black/5">
                        <div className="font-bold text-gray-500 text-[9px] uppercase mb-0.5">Remarks</div>
                        <div
                            className="text-[9px] text-gray-600 leading-tight max-h-20 overflow-y-auto pr-1 custom-scrollbar"
                            dangerouslySetInnerHTML={{ __html: info.remarks || info.tandc }}
                        />
                    </div>
                )}
            </div>
        </div>
    );

    // If we have no details and we aren't loading, hide completely
    if (!loading && (!details || details.length === 0)) return null;

    return (
        <div className="mb-2">
            <button
                onClick={() => setShowModal(true)}
                disabled={loading}
                className="w-full flex items-center justify-between p-2.5 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg transition-all group shadow-sm disabled:opacity-50"
            >
                <div className="flex items-center gap-3">
                    <div className={`p-1.5 bg-white text-blue-600 rounded-md group-hover:scale-110 transition-transform ${loading ? 'animate-spin' : ''}`}>
                        <Link size={14} />
                    </div>
                    <div className="flex flex-col items-start translate-y-[1px]">
                        <span className="font-bold text-xs text-blue-800">Discount Asked From</span>
                        <span className="text-[10px] text-blue-600 font-medium opacity-80">
                            {quoteId.split(',').map(id => id.trim()).filter(id => id && id !== "null").reverse().join(' → ')}
                        </span>
                    </div>
                </div>
                <div className="bg-blue-600 text-white text-[10px] font-bold px-2.5 py-1 rounded-full flex items-center gap-1.5 shadow-sm">
                    {loading ? "Loading..." : "View Comparison"}
                    <span className="text-white/50">|</span>
                    <Expand size={12} />
                </div>
            </button>

            <AnimatePresence>
                {showModal && (
                    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                            onClick={() => setShowModal(false)}
                        />
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95, y: 20 }}
                            className="relative w-full max-w-6xl max-h-[90vh] bg-white rounded-xl shadow-2xl flex flex-col overflow-hidden"
                        >
                            {/* Modal Header */}
                            <div className="flex items-center justify-between p-4 border-b bg-gray-50">
                                <div>
                                    <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                                        <span className="p-1.5 bg-blue-100 text-blue-600 rounded-lg"><Link size={18} /></span>
                                        Quote Comparison View
                                    </h3>
                                    <p className="text-xs text-gray-500 mt-0.5 px-0.5">Comparing Current Quote vs Original Discounted Versions</p>
                                </div>
                                <button
                                    onClick={() => setShowModal(false)}
                                    className="p-2 hover:bg-red-50 text-gray-400 hover:text-red-500 rounded-full transition-colors"
                                >
                                    <X size={20} />
                                </button>
                            </div>

                            {/* Tabs Navigation */}
                            <div className="flex items-center gap-1 px-4 border-b bg-white">
                                {[
                                    { id: "details", label: "Details", icon: <File size={14} /> },
                                    { id: "feasibility", label: "Feasibility", icon: <CheckCircle size={14} /> },
                                    { id: "communication", label: "Communication Hub", icon: <MessageSquare size={14} /> }
                                ].map((tab) => (
                                    <button
                                        key={tab.id}
                                        onClick={() => setMainTab(tab.id)}
                                        className={`flex items-center gap-2 px-6 py-3 text-xs font-bold transition-all border-b-2 ${mainTab === tab.id
                                            ? "border-blue-600 text-blue-600 bg-blue-50/50"
                                            : "border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                                            }`}
                                    >
                                        {tab.icon}
                                        {tab.label}
                                    </button>
                                ))}
                            </div>

                            {/* Modal Body */}
                            <div className="flex-1 overflow-y-auto p-4 lg:p-6 bg-slate-50/30">
                                {mainTab === "details" && (
                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 items-start">
                                        {/* Current Side */}
                                        <div className="space-y-4">
                                            <div className="flex items-center justify-between px-1">
                                                <h4 className="text-sm font-black flex items-center gap-2 text-blue-700">
                                                    CURRENT QUOTE DETAILS
                                                    <span className="h-2 w-2 rounded-full bg-blue-500 animate-pulse"></span>
                                                </h4>
                                                <span className="text-[10px] font-bold bg-blue-100 text-blue-700 px-2 py-0.5 rounded uppercase">
                                                    ID: {currentQuote.quoteid || currentQuote.quote_id}
                                                </span>
                                            </div>

                                            <div className="bg-white p-4 rounded-xl border border-blue-100 shadow-sm space-y-4">
                                                {(() => {
                                                    const cBasic = parsePlanJson(currentQuote.basic_info);
                                                    const cStandard = parsePlanJson(currentQuote.standard_info);
                                                    const cAdvanced = parsePlanJson(currentQuote.advanced_info);
                                                    const cSelected = (currentQuote.selected_plans || "").split(",").map(p => p.trim()).filter(Boolean);

                                                    return (
                                                        <>
                                                            <div className="flex flex-col gap-2 pb-2 border-b border-gray-50 mb-2">
                                                                <span className="text-[10px] bg-gray-100 w-fit text-gray-700 px-2 py-0.5 rounded font-bold uppercase">
                                                                    Quote ID: {currentQuote.quoteid}
                                                                </span>
                                                                {currentQuote.quote_heading && (
                                                                    <span className="text-xs font-semibold text-gray-500 truncate italic">
                                                                        {currentQuote.quote_heading}
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <div className="grid grid-cols-1 md:grid-cols-1 gap-3">
                                                                {cSelected.includes("Basic") && cBasic && renderComparePlan("Basic", cBasic)}
                                                                {cSelected.includes("Standard") && cStandard && renderComparePlan("Standard", cStandard)}
                                                                {cSelected.includes("Advanced") && cAdvanced && renderComparePlan("Advanced", cAdvanced)}
                                                            </div>
                                                        </>
                                                    );
                                                })()}
                                            </div>
                                        </div>

                                        {/* Original Side */}
                                        <div className="space-y-4">
                                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-1">
                                                <h4 className="text-sm font-black text-gray-700">ORIGINAL QUOTE DETAILS</h4>
                                                <div className="flex flex-wrap gap-1">
                                                    {details.map((quote, idx) => (
                                                        <button
                                                            key={idx}
                                                            onClick={() => setActiveOriginalTab(idx)}
                                                            className={`px-3 py-1 text-[10px] font-bold rounded-full transition-all border ${activeOriginalTab === idx
                                                                ? "bg-gray-800 text-white border-gray-800 shadow-sm"
                                                                : "bg-white text-gray-600 border-gray-200 hover:border-gray-400"
                                                                }`}
                                                        >
                                                            {quote.quoteid}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>

                                            <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm space-y-4 relative">
                                                {details[activeOriginalTab] && (() => {
                                                    const quote = details[activeOriginalTab];
                                                    const oBasic = parsePlanJson(quote.basic_info);
                                                    const oStandard = parsePlanJson(quote.standard_info);
                                                    const oAdvanced = parsePlanJson(quote.advanced_info);
                                                    const oSelected = (quote.selected_plans || "").split(",").map(p => p.trim()).filter(Boolean);

                                                    return (
                                                        <>
                                                            <div className="flex flex-col gap-2 pb-2 border-b border-gray-50 mb-2">
                                                                <span className="text-[10px] w-fit bg-gray-100 text-gray-700 px-2 py-0.5 rounded font-bold uppercase">
                                                                    Quote ID: {quote.quoteid}
                                                                </span>
                                                                {quote.quote_heading && (
                                                                    <span className="text-xs font-semibold text-gray-500 truncate italic">
                                                                        {quote.quote_heading}
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <div className="grid grid-cols-1 md:grid-cols-1 gap-3">
                                                                {oSelected.includes("Basic") && oBasic && renderComparePlan("Basic", oBasic, true)}
                                                                {oSelected.includes("Standard") && oStandard && renderComparePlan("Standard", oStandard, true)}
                                                                {oSelected.includes("Advanced") && oAdvanced && renderComparePlan("Advanced", oAdvanced, true)}
                                                            </div>
                                                        </>
                                                    );
                                                })()}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {mainTab === "feasibility" && (
                                    <>
                                        {loadingFeasibility && !feasibilityData ? (
                                            <div className="flex justify-center py-10">
                                                <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                                            </div>
                                        ) : (() => {
                                            const feasibilityQuotes = [currentQuote, ...details].filter(q =>
                                                q && (q.isfeasability == 1 || q.isfeasability == "1") &&
                                                q.feasability_status === "Completed" &&
                                                q.feasability_comments
                                            );

                                            const uniqueFeasibilityQuotes = feasibilityQuotes.filter((q, index, self) =>
                                                index === self.findIndex((t) => (t.quoteid || t.quote_id) === (q.quoteid || q.quote_id))
                                            );

                                            const hasFeasibilityData = feasibilityData && feasibilityData.length > 0;
                                            const hasQuoteComments = uniqueFeasibilityQuotes.length > 0;

                                            if (!hasFeasibilityData && !hasQuoteComments) {
                                                return (
                                                    <div className="flex flex-col items-center justify-center py-20 text-gray-500">
                                                        <File size={40} className="mb-4 opacity-20" />
                                                        <p className="font-semibold">No feasibility comments available.</p>
                                                    </div>
                                                );
                                            }

                                            return (
                                                <div className="max-w-4xl mx-auto space-y-6">
                                                    <h4 className="text-sm font-black flex items-center gap-2 text-gray-700 px-1">
                                                        <File size={16} className="text-emerald-600" /> FEASIBILITY COMMENTS
                                                    </h4>
                                                    <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm space-y-6">
                                                        {hasFeasibilityData && (
                                                            <div className="border border-emerald-100 rounded-lg p-4 bg-emerald-50/50 relative">
                                                                <div className="absolute -top-2.5 left-4 bg-white px-2 py-0.5 text-[10px] font-bold text-emerald-700 border border-emerald-200 rounded-full shadow-sm">
                                                                    Ref ID: {refId}
                                                                </div>
                                                                <div className="mt-2 text-sm text-gray-700 whitespace-pre-wrap leading-relaxed" dangerouslySetInnerHTML={{ __html: feasibilityData }} />
                                                            </div>
                                                        )}
                                                        {uniqueFeasibilityQuotes.map((q, idx) => (
                                                            <div key={idx} className="border border-emerald-100 rounded-lg p-4 bg-emerald-50/50 relative">
                                                                <div className="absolute -top-2.5 left-4 bg-white px-2 py-0.5 text-[10px] font-bold text-emerald-700 border border-emerald-200 rounded-full shadow-sm">
                                                                    Quote ID: {q.quoteid || q.quote_id}
                                                                </div>
                                                                <div className="mt-2 text-sm text-gray-700 whitespace-pre-wrap leading-relaxed" dangerouslySetInnerHTML={{ __html: q.feasability_comments }} />
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            );
                                        })()}
                                    </>
                                )}

                                {mainTab === "communication" && (
                                    <>
                                        {loadingChats ? (
                                            <div className="flex justify-center items-center py-20">
                                                <div className="flex flex-col items-center gap-2">
                                                    <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                                                    <span className="text-sm font-semibold text-gray-500">Loading communication history...</span>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="max-w-4xl mx-auto">
                                                <h4 className="text-sm font-black flex items-center gap-2 text-gray-700 mb-4 px-1">
                                                    <MessageSquare size={16} className="text-blue-600" /> COMMUNICATION HISTORY
                                                </h4>
                                                {chatsData && ((Array.isArray(chatsData) && chatsData.length > 0) || (!Array.isArray(chatsData) && Object.keys(chatsData).length > 0)) ? (
                                                    <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm space-y-6">
                                                        {(() => {
                                                            let flatChats = [];
                                                            if (Array.isArray(chatsData)) {
                                                                flatChats = chatsData.flatMap(item => {
                                                                    if (item.message) return [item];
                                                                    const arrKey = Object.keys(item).find(k => Array.isArray(item[k]));
                                                                    if (arrKey) {
                                                                        return item[arrKey].map(c => ({ ...c, quote_id: c.quote_id || item.quote_id }));
                                                                    }
                                                                    return [];
                                                                });
                                                            } else if (typeof chatsData === 'object') {
                                                                flatChats = Object.entries(chatsData).flatMap(([k, v]) => {
                                                                    if (Array.isArray(v)) return v.map(c => ({ ...c, quote_id: c.quote_id || k }));
                                                                    return [];
                                                                });
                                                            }

                                                            if (flatChats.length === 0) return <div className="text-center py-10 text-gray-500 font-medium">No communication history found.</div>;

                                                            const grouped = flatChats.reduce((acc, chat) => {
                                                                const qId = chat.quote_id || "General";
                                                                if (!acc[qId]) acc[qId] = [];
                                                                acc[qId].push(chat);
                                                                return acc;
                                                            }, {});

                                                            return Object.entries(grouped).map(([qId, chats], qIdx) => (
                                                                <div key={qIdx} className={`border border-gray-100 rounded-lg p-4 bg-gray-50/50 relative ${qIdx > 0 ? 'mt-6' : ''}`}>
                                                                    <div className="absolute -top-2.5 left-4 bg-white px-2 py-0.5 text-[10px] font-bold text-blue-700 border border-blue-200 rounded-full shadow-sm">
                                                                        Quote ID: {qId}
                                                                    </div>
                                                                    <div className="mt-2">
                                                                        {renderChatList(chats)}
                                                                    </div>
                                                                </div>
                                                            ));
                                                        })()}
                                                    </div>
                                                ) : (
                                                    <div className="flex flex-col items-center justify-center py-20 text-gray-500 bg-white rounded-xl border border-gray-200">
                                                        <MessageSquare size={40} className="mb-4 opacity-20" />
                                                        <p className="font-semibold">No communication history found.</p>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>

                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
};



export default OriginalQuoteDetails;
